import {
  AlignmentType,
  Document, Footer, Header, HorizontalPositionAlign, LeaderType,
  Media, PageBreak,
  PageNumber, PageNumberFormat,
  Paragraph, StyleLevel,
  Table, TableCell, TableLayoutType, TableOfContents, TableRow,
  TextRun, TextWrappingSide, TextWrappingType, WidthType,
} from "docx";
import solicitorSpecLogo from "../../assets/base64images/solicitorSpecLogo";
import solicitorSpecLastPage
  from "../../assets/base64images/solicitorSpecLastPage";
import moment from "moment";
import solicitorSpecFirstPageSlope
  from "../../assets/base64images/solicitorSpecFirstPageSlope";
import solicitorSpecFirstPageManOnly
  from "../../assets/base64images/solicitorSpecFirstPageManOnly";
import { LEGAL_ADVICE_TEXT, POLICYHOLDER_TEXT } from "../variables";

export class DocumentCreator  {
  create([columns, attachmentsColumns, questionsTableData, basicDataTableData, attachmentsDataTableData, opponentsTableData,medicalExpertsTableData]) {
    const document = new Document({ styles: {
        paragraphStyles: [
          {
            //this is the default style of the document
            // Only `name` prop required, `id` not necessary
            id: 'Normal',
            name: 'Normal',
            run: {
              font: "Calibri",
              size: 24,
            },
            paragraph: {
              spacing: { line: 264, after: 120, before: 240 },
            },
          },
          {
            id: 'code',
            name: 'code',
            run: {
              font: "Courier New",
              size: 16,
            },
            paragraph: {
              spacing: { after: 60, before: 60 },
            },
          },
          {
            //Table - Table Heading
            id: "tableHeaderStyle",
            name: "tableHeaderStyle",
            basedOn: 'Normal',
            run: {
              color: "FFFFFF",
              italics: false,
              font: "Calibri",
              bold: 'true',
              size: 22,
            },
            paragraph: {
              spacing: { line: 270, after: 120, before: 120 }, // note sure about line
              italics: false,
            },
          },
          {
            //Table - Table Content
            id: "tableCellStyle",
            name: "tableCellStyle",
            basedOn: 'Normal',
            run: {
              italics: false,
              font: "Calibri",
              size: 22,
            },
            paragraph: {
              italics: false,
              spacing: { line: 265, after: 120, before: 120 }, // note sure about line
            },
          },
          {
            id: "TOC 1",
            name: "TOC 1",
            basedOn: "Normal",
            run: {
              size: 28,
            },
            paragraph: {
              spacing: { before: 480, after: 120 , },
              keepLines: true,
              tabStop: { right: 9620, leader: LeaderType.DOT},
            },
          },
          {
            id: "TOC 2",
            name: "TOC 2",
            basedOn: "Normal",
            run: {
              size: 24,
            },
            paragraph: {
              spacing: { before: 240, after: 120, },
              indent: { left: 565 },
              keepLines: true,
              tabStop: { right: 9620, leader: LeaderType.DOT},
            },
          },
          {
            id: "footer",
            name: "footer",
            quickFormat: true,
            run: {
              font: "Calibri",
              size: 28,
              color: "FFFFFF",
              bold: true
            },
          },
          {
            id: 'Document Heading',
            name: 'Document Heading',
            run: {
              font: "Calibri",
              size: 64,
              color: "FFFFFF",
              bold: true,
            },
            paragraph: {
              spacing: { line: 215, after: 0, before: 12600  },
            },
          },
          {
            id: 'Document Subheading',
            name: 'Document Subheading',
            run: {
              font: "Calibri",
              size: 40,
              color: "FFFFFF",
              bold: true,
            },
            paragraph: {
              spacing: { after: 0 ,before: 120, },
              alignment: HorizontalPositionAlign.RIGHT,
            },
          },
          {
            id: 'Chapter - Heading',
            name: 'Chapter - Heading',
            outlineLevel: 0,
            run: {
              font: "Calibri Light",
              size: 36,
              color: "2E74B5",
              bold: true,
              outlineLevel: 0,
            },
            paragraph: {
              spacing: { after: 720, before: 900,  },
              outlineLevel: 0,
            },
          },
          {
            id: 'Chapter - Skipped Heading',
            name: 'Chapter - Skipped Heading',
            run: {
              font: "Calibri Light",
              size: 36,
              color: "2E74B5",
              bold: true,
            },
            paragraph: {
              spacing: { after: 720, before: 900,  },
            },
          },
          {
            id: 'Section - Heading',
            name: 'Section - Heading',
            basedOn: 'heading2',
            outlineLevel: 1,
            run: {
              font: "Calibri Light",
              size: 28,
              color: "2E74B5",
              bold: true,
              outlineLevel: 1,
            },
            paragraph: {
              indent: { left: 550 },
              spacing: { after: 360, before: 720,  },
              outlineLevel: 1,
            },
          },
          {
            id: 'Chapter - Page Break',
            name: 'Chapter - Page Break',
            run: {
              font: "Calibri Light",
              size: 16,
              color: "2E74B5",
            },
            paragraph: {
              pageBreakBefore: true,
              keepNext: true
            },
          },
          {
            id: 'Header',
            name: 'Header',
            run: {
              font: "Calibri Light",
              size: 36,
              color: "2E74B5",
              bold: true,
            },
            paragraph: {
              spacing: { after: 720, before: 900,  },
            },
          },
          {
            id: 'Bulleted List',
            name: 'Bulleted List',
            basedOn: 'Normal',
            run: {
              font: "Calibri",
              size: 24,
            },
            paragraph: {
              spacing: { before: 120 },
              indent: {left: 286, hanging: 286, right: 568},
              keepLines: true,
              leftTabStop: 100,
            },
          }
        ],
      }});

    const acceptableFileFormats = ['Adobe Acrobat - .pdf', 'Microsoft Word - .doc, .docx, .dot', 'Microsoft Excel - .xls, .xlsx', 'Image - .jpg, jpeg, .bmp, .png', 'Microsoft Outlook - .msg, .eml', 'Plain Text - .txt'];
    const logoImage = Media.addImage(document, solicitorSpecLogo,140.8,145,{
      floating: {
        horizontalPosition: {
          offset: 6100000, // relative: HorizontalPositionRelativeFrom.PAGE by default
        },
        verticalPosition: {
          offset: 100000, // relative: VerticalPositionRelativeFrom.PAGE by default
        },
        wrap: {
          type: TextWrappingType.SQUARE,
          side: TextWrappingSide.BOTH_SIDES,
        },
        margins: {
          top: 179857,
          bottom: 179857,
          left: 179857,
          right: 179857
        },
        layoutInCell: false,

      },
    });
    const mainImageOnlyMan = Media.addImage(document,solicitorSpecFirstPageManOnly,810,1060,
      {
        zIndex: 5,
        floating: {
          zIndex: 5,
          horizontalPosition: {
            offset: 1000, // relative: HorizontalPositionRelativeFrom.PAGE by default
          },
          verticalPosition: {
            offset: -25000, // relative: VerticalPositionRelativeFrom.PAGE by default
          },
          behindDocument: true,
          allowOverlap: true,
          layoutInCell: false,
        },
      });
    const firstPageSlopeImage = Media.addImage(document, solicitorSpecFirstPageSlope, 1010, 600,
      {
        zIndex: 10,
        floating: {
          zIndex: 10,
          horizontalPosition: {
            offset: 1000,
          },
          verticalPosition: {
            offset: 7400000,
          },
          behindDocument: true,
          allowOverlap: true,
          layoutInCell: false,
        },
      });
    const slopeImage = Media.addImage(document, solicitorSpecFirstPageSlope, 810, 300,
      {
        floating: {
          horizontalPosition: {
            offset: 1000, // relative: HorizontalPositionRelativeFrom.PAGE by default
          },
          verticalPosition: {
            offset: 9300000, // relative: VerticalPositionRelativeFrom.PAGE by default
          },
          behindDocument: true,
          allowOverlap: true,
          wrap: {
            type: TextWrappingType.SQUARE,
            side: TextWrappingSide.BOTH_SIDES,
          },
          margins: {
            top: 179857,
            bottom: 179857,
            left: 179857,
            right: 179857
          },
          layoutInCell: false,
        },

      });

    const lastPageImage = Media.addImage(document,solicitorSpecLastPage,810,1210, {
      floating: {
        horizontalPosition: {
          offset: 1000,
        },
        verticalPosition: {
          offset: 1000,
        },
        behindDocument: false,
        layoutInCell: false,
      },
    });
    const tableGeneration = (columnHeaders,tableData) => new Table({
      alignment: 'center',
      layout: TableLayoutType.FIXED,
      margins: {
        top: 0,
        right: 85,
        bottom: 0,
        left: 85,
      },
      rows:[
        new TableRow({
          cantSplit: true,
          tableHeader: true,
          children:
            columnHeaders.map((c,index) => new TableCell({
              shading: {
                fill: 'fcba03'
              },
              width: { size: (columnHeaders.length === 4 ? 2438 : (index===2? 4082 : 2835)), type: WidthType.DXA },
              children: [
                new Paragraph({
                    style: "tableHeaderStyle",
                    children:
                      c.header.split("\n").map((line, index) =>
                        new TextRun({
                          text: line,
                          break: index !== 0,
                        })
                      )
                  }
                )],
            }))
        }),
        ...tableData.filter(row => row.isMandatory !== 'No').map(row =>
          new TableRow({
            cantSplit: true,
            children: [
              new TableCell({
                width: { size: (columnHeaders.length === 4 ? 2438 : 2835), type: WidthType.DXA },
                children: [new Paragraph({
                  text: row.questionMachineName,
                  style: 'tableCellStyle'
                })
                ],
              }),
              row.questionName &&
              new TableCell({
                width: { size: (columnHeaders.length === 4 ? 2438 : 2835), type: WidthType.DXA },
                children: [new Paragraph({
                  text: row.questionName,
                  style: 'tableCellStyle'
                })],
              }),
              row.isMandatory ?
                new TableCell({
                  width: { size: 2438, type: WidthType.DXA },
                  children: [new Paragraph({
                    style: 'tableCellStyle',
                    children:
                      row.isMandatory
                        .split("\n")
                        .map((item, index) => new TextRun({
                          text: item,
                          break: index !== 0,
                        })),
                  })]})
                : null,
              new TableCell({
                width: {size: (columnHeaders.length === 4 ? 2438 : 4082), type: WidthType.DXA},
                children: [new Paragraph({
                  children: row.allowedValues
                    .split("\n")
                    .map((item, index) => new TextRun({
                      text: item,
                      break: index !== 0,
                    })),
                  style: 'tableCellStyle',
                })]
              }),
            ],
          })
        )
      ],
    });
    const basicDataTable = tableGeneration(columns,basicDataTableData);
    const medicalExpertsDataTable = tableGeneration(columns,medicalExpertsTableData);
    const opponentsDataTable = tableGeneration(columns,opponentsTableData);
    const questionsDataTable = tableGeneration(columns,questionsTableData);
    const attachmentsDataTable = tableGeneration(attachmentsColumns,attachmentsDataTableData);

    const showOpponents = basicDataTableData.some(row => row.questionMachineName === "ApplicationOpponents" && row.isMandatory !== 'No');
    const showMedicalExperts = basicDataTableData.some(row => row.questionMachineName === "ApplicationMedicalExpertFees" && row.isMandatory !== 'No');

    document.addSection({
      margins: {
        top: 0,
        right: 1135,
        bottom: 10,
        left: 1135,
        header: 0,
        footer: 10,
      },
      headers: {
        default: new Header({
          children: [new Paragraph({text: ''})],
        }),
      },
      footers: {
        default: new Footer({
          children: [new Paragraph({text: ''})],
        }),
      },
      children: [
        new Paragraph({
          style: 'Document Heading',
          alignment: 'left',
          children: [
            mainImageOnlyMan,
            logoImage,
            firstPageSlopeImage,
            new TextRun({
              children: ['Client specifications for ARAG Policy-Inception integration with CLIENTNAME'],
            }),
          ],
        }),
        new Paragraph({
          text: moment().format('DD MMMM YYYY'),
          style: 'Document Subheading',
          spacing: {
            before: 10,
            after: 0
          }
        })
      ]
    });
    document.addSection({
      properties: {
        pageNumberStart: 1,
        pageNumberFormatType: PageNumberFormat.DECIMAL,
      },
      margins: {
        top: 850,
        right: 1135,
        bottom: 1985,
        left: 1135,
        header: 566,
        footer: 566,
      },
      headers: {
        default: new Header({
          alignment: AlignmentType.LEFT,
          children: [new Paragraph(logoImage)],
        }),
      },
      footers: {
        default: new Footer({
          children: [
            new Paragraph({
              style: 'footer',
              rightTabStop: 9070,
              children: [
                slopeImage,
                new TextRun({
                  children: [`${POLICYHOLDER_TEXT} specifications for ARAG Policy-Inception`],
                }),
                new TextRun({
                  text: "",
                  break: true,
                }),
                new TextRun({
                  children: ['integration with _NAME_\t'],
                }),
                new TextRun({
                  size: 22,
                  children: ["Page ", PageNumber.CURRENT, ' of ', PageNumber.TOTAL_PAGES_IN_SECTION],
                }),
              ],
            })
          ],
        }),
      },
      children: [
        new Paragraph({style: "Chapter - Page Break"}),
        new Paragraph({
          text: 'Contents',
          style: "Chapter - Skipped Heading",
        }),
        new TableOfContents("", {
          // hyperlink: true,
          headingStyleRange: "1-4",
          // leader: 2,
          // entryAndPageNumberSeparator:".",
          // entryAndPageNumberSeparator: "..............................................................",
          // useAppliedParagraphOutlineLevel: true,
          stylesWithLevels: [
            new StyleLevel("TOC 1", 1),
            new StyleLevel("TOC 2", 2),
            new StyleLevel("TOC 2", 3),
            new StyleLevel("TOC 2", 4)
          ],
        }),
        new Paragraph({style: "Chapter - Page Break"}),
        this.createHeading("Introduction and Overview",),
        new Paragraph({
          children: [
            new TextRun({
              text: "ARAG\'s gATEway portal provides solicitors with the ability to create their own ATE risks and manage their policies online. Additionally, applications can be submitted to ARAG via the gATEway integration service.",
            })
          ]
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'This document specifies the ',
            }),
            new TextRun({
              text: 'minimum ',
              bold: true,
            }),
            new TextRun({
              text: 'data required for a business partner’s system(s) to submit one or more application(s) for policy inception. It is intended to supplement the ARAG document ',
            }),
            new TextRun({
              text: `”${LEGAL_ADVICE_TEXT} Enhanced ATE Integration – ${LEGAL_ADVICE_TEXT} Engagement”`,
              bold: true,
            }),
            new TextRun({
              text: ', but focusing on Policy Inception and the data specific to individual clients.',
            })
          ]
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'This document is targeted for ARAG’s ATE business partner CLIENTNAME, specifically their technical departments, and may be distributed externally.',
              bold: true,
            })
          ]
        }),
        new Paragraph({style: "Chapter - Page Break"}),
        this.createHeading("Policy Inception (REST SOAP) Data Specifications"),
        new Paragraph({
          children: [
            new TextRun({
              text: 'The tables below set out the Request Attributes to be used for the REST SOAP call. These attributes are required for ',
            }),
            new TextRun({
              text: 'all',
              bold: true,
            }),
            new TextRun({
              text: ' applications.',
            }),
          ]
        }),
        this.createSubHeading('Basic data structure', false),
        new Paragraph({
          text: 'The basic data table below is set of top-level data in the REST SOAP call, and its specified attributes are required for all applications. Nested data is detailed in the subsequent sections. Note that all fields should be listed alphabetically within the call.',
        }),
        basicDataTable,
        new Paragraph({}),
        new Paragraph({
          children: [
            new PageBreak({})
          ]
        }),
        this.createSubHeading('Attachments data sub-structure'),
        new Paragraph({
          text: 'The attachments table below is set of nested data which is an array containing the base64 representation of files accompanying the application. It can have zero or more entries. Note that all fields within each attachment object should be listed alphabetically. Each entry has the following structure:',
        }),
        attachmentsDataTable,
        new Paragraph({}),
        new Paragraph({
          text: 'Acceptable file formats include:\n',
        }),
        ...acceptableFileFormats
          .map((bullet) => {
            const arr = [];
            arr.push(this.createBullet(bullet));
            return arr;
          })
          .reduce((prev, curr) => prev.concat(curr), []),
        showOpponents && new Paragraph({
          children: [
            new PageBreak({})
          ]
        }),
        showOpponents && this.createSubHeading('Opponents data sub-structure'),
        showOpponents && new Paragraph({
          text: 'The Application Opponents table below is set of nested data which is an array of information about each opponent. It can have zero or more entries. Note that all fields within each opponent object should be listed alphabetically. Each entry has following structure:',
        }),
        showOpponents && opponentsDataTable,
        showOpponents &&
        new Paragraph({}),
        showMedicalExperts && new Paragraph({
          children: [
            new PageBreak({})
          ]
        }),
        showMedicalExperts && this.createSubHeading('Medical experts data sub-structure'),
        showMedicalExperts && new Paragraph({
          text: 'The Medical Experts table below is set of nested data which is an array of information about each Medical Expert and their fees. It can have zero or more entries. Note that all fields within each medical expert object should be listed alphabetically. Each entry has the following structure.',
        }),
        showMedicalExperts && medicalExpertsDataTable,
        showMedicalExperts &&
        new Paragraph({}),
        new Paragraph({
          children: [
            new PageBreak({})
          ]
        }),
        this.createSubHeading('Application answers data sub-structure'),
        new Paragraph({
          text: 'The questions and answers below are dependent on the settings configured by ARAG’s Underwriting department per Case Type. Certain answers will also trigger an automatic referral to ARAG’s Underwriting department which will require their review prior to policy creation. When this happens, you will see in the SOAP response parameters (examples of these are included in the ‘Solicitor Enhanced ATE Integration – Solicitor Engagement’ document) that the application(s) have been referred.',
        }),
        questionsDataTable,
        new Paragraph({}),
        new Paragraph({style: "Chapter - Page Break"}),
        this.createHeading("XML Sample"),
        new Paragraph({
          children: [
            new TextRun({
              text: 'You can use this link ',
            }),
            document.createHyperlink('https://developer.arag.co.uk/V1_Sandbox_SOAP/SoapService.svc?wsdl', 'https://developer.arag.co.uk/V1_Sandbox_SOAP/SoapService.svc?wsdl'),
            new TextRun({
              text: ' to generate an XML sample in SoapUI or similar software. This will create a sample structure as per below. Please note that the XML tags must be in alphabetical order for all sections except the Application Answers section. A full sample is embedded here: ',
            }),

          ]
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: ' _EMBEDED_FILE_ ',
            }),
          ]
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Additionally, a small sample including the beginning and end of the SOAP contact is shown here:  ',
            }),
          ]
        }),

        new Paragraph({
          style: "code",
          text: ' <soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:tem="http://tempuri.org/" xmlns:gat="http://schemas.datacontract.org/2004/07/Gateway.DLL.Dtos" xmlns:arr="http://schemas.microsoft.com/2003/10/Serialization/Arrays">',
        }),
        new Paragraph({
          style: "code",
          text: '   <soapenv:Header/>\n',
        }),
        new Paragraph({
          style: "code",
          text: '   <soapenv:Body>\n',
        }),
        new Paragraph({
          style: "code",
          text: '      <tem:Applications>',
        }),
        new Paragraph({
          style: "code",
          text: '         <tem:username>youremail@email.com</tem:username>',
        }),
        new Paragraph({
          style: "code",
          text: '         <tem:password>yourpassword</tem:password>',
        }),
        new Paragraph({
          style: "code",
          text: '         <tem:soapApplicationFormDtos>',
        }),
        new Paragraph({
          style: "code",
          text: '            <!--Zero or more repetitions:-->',
        }),
        new Paragraph({
          style: "code",
          text: '            <gat:SoapApplicationFormDto>',
        }),
        new Paragraph({
          style: "code",
          text: '               <gat:Address>SOAPtest</gat:Address>',
        }),
        new Paragraph({
          style: "code",
          text: '               <gat:ApplicationAnswers>',
        }),
        new Paragraph({
          style: "code",
          text: '                  <arr:KeyValueOfstringstring>',
        }),
        new Paragraph({
          style: "code",
          text: '                     <arr:Key>CaseStage</arr:Key>',
        }),
        new Paragraph({
          style: "code",
          text: '                     <arr:Value>Post-Issue</arr:Value>',
        }),
        new Paragraph({
          style: "code",
          text: '                  </arr:KeyValueOfstringstring>',
        }),
        new Paragraph({
          style: "code",
          text: '                   <arr:KeyValueOfstringstring>',
        }),
        new Paragraph({
          style: "code",
          text: '                     <arr:Key>LikelyClaimsTrack</arr:Key>',
        }),
        new Paragraph({
          style: "code",
          text: '                     <arr:Value>Fast Track</arr:Value>',
        }),
        new Paragraph({
          style: "code",
          text: '                  </arr:KeyValueOfstringstring>',
        }),

        new Paragraph({
          children: [
            new TextRun({
              text: '…and so on until…',
            }),
          ]
        }),
        new Paragraph({
          text: '</gat:SoapApplicationFormDto>',
          style: "code",
        }),
        new Paragraph({
          text: '         </tem:soapApplicationFormDtos>',
          style: "code",
        }),
        new Paragraph({
          text: '      </tem:Applications>',
          style: "code",
        }),
        new Paragraph({
          text: '   </soapenv:Body>',
          style: "code",
        }),
        new Paragraph({
          text: '</soapenv:Envelope>',
          style: "code",
        }),
        new Paragraph({style: "Chapter - Page Break"}),
        this.createHeading("Technical Support"),
        new Paragraph({
          children: [
            new TextRun({
              text: 'For technical help, please contact ARAG via the  ',
            }),
            document.createHyperlink('mailto:software@arag.co.uk', 'software@arag.co.uk'),
            new TextRun({
              text: ' e-mail address.',
            }),
          ]
        })
      ],
    });
    document.addSection({
      children: [
        new Paragraph(lastPageImage)
      ]
    });
    return document;
  }
  createHeading(text) {
    return new Paragraph({
      text: text,
      style: 'Chapter - Heading',
      // thematicBreak: true,
    });
  }
  createSubHeading(text, pageBreak = true) {
    return new Paragraph({
      text: text,
      style: 'Section - Heading',
    });
  }
  createRoleText(roleText) {
    return new Paragraph({
      children: [
        new TextRun({
          text: roleText,
          italics: true,
        }),
      ],
    });
  }
  createBullet(text) {
    return new Paragraph({
      children: [
        new TextRun({
          text: '•\t',
          color: "2E74B5",
          bold: true,
        }),
        new TextRun({
          text: text,
        }),
      ],
      style: 'Bulleted List',
    });
  }
  // tslint:disable-next-line:no-any
  createSkillList(skills) {
    return new Paragraph({
      children: [new TextRun(skills.map((skill) => skill.name).join(", ") + ".")],
    });
  }
  // tslint:disable-next-line:no-any
  createAchivementsList(achivements) {
    return achivements.map(
      (achievement) =>
        new Paragraph({
          text: achievement.name,
          bullet: {
            level: 0,
          },
        }),
    );
  }
  splitParagraphIntoBullets(text) {
    return text.split("\n\n");
  }
  // tslint:disable-next-line:no-any
  createPositionDateText(startDate, endDate, isCurrent) {
    const startDateText = this.getMonthFromInt(startDate.month) + ". " + startDate.year;
    const endDateText = isCurrent ? "Present" : `${this.getMonthFromInt(endDate.month)}. ${endDate.year}`;

    return `${startDateText} - ${endDateText}`;
  }
  getMonthFromInt(value) {
    switch (value) {
      case 1:
        return "Jan";
      case 2:
        return "Feb";
      case 3:
        return "Mar";
      case 4:
        return "Apr";
      case 5:
        return "May";
      case 6:
        return "Jun";
      case 7:
        return "Jul";
      case 8:
        return "Aug";
      case 9:
        return "Sept";
      case 10:
        return "Oct";
      case 11:
        return "Nov";
      case 12:
        return "Dec";
      default:
        return "N/A";
    }
  }
}
